import React, { useState, useEffect } from 'react';
import { connectorService } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';
import MintsoftConnectionForm from './MintsoftConnectionForm';
import {
  Plus,
  RefreshCw,
  ShoppingCart,
  AlertCircle,
  CheckCircle,
  Clock,
  Trash2,
  Lock
} from 'lucide-react';

function MintsoftConnectorsTab() {
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const { notify } = useNotification();

  useEffect(() => {
    fetchConnections();
  }, []);

  const fetchConnections = async () => {
    try {
      setLoading(true);
      const response = await connectorService.listMintsoftConnections();
      setConnections(response || []);
    } catch (err) {
      console.error('Error fetching Mintsoft connections:', err);
      notify.error('Failed to load Mintsoft connections');
    } finally {
      setLoading(false);
    }
  };

  const handleConnectionCreated = () => {
    setShowForm(false);
    fetchConnections();
  };

  const handleSync = async (connectionId) => {
    try {
      await connectorService.syncMintsoftConnection(connectionId);
      notify.success('Sync started! Data extraction running in background.');
      setTimeout(fetchConnections, 2000);
    } catch (err) {
      console.error('Error syncing:', err);
      notify.error('Failed to trigger sync');
    }
  };

  const handleDelete = async (connectionId) => {
    if (!window.confirm('Delete this Mintsoft connection? This will stop all data syncing.')) {
      return;
    }

    try {
      await connectorService.deleteMintsoftConnection(connectionId);
      notify.success('Connection deleted');
      fetchConnections();
    } catch (err) {
      console.error('Error deleting:', err);
      notify.error('Failed to delete connection');
    }
  };

  if (showForm) {
    return (
      <MintsoftConnectionForm
        onSuccess={handleConnectionCreated}
        onBack={() => setShowForm(false)}
      />
    );
  }

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin h-8 w-8 border-2 border-primary-600 dark:border-primary-500 border-t-transparent rounded-full mx-auto mb-4"></div>
        <p className="text-slate-600 dark:text-slate-400">Loading connections...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-slate-900 dark:text-white flex items-center">
            <ShoppingCart className="w-6 h-6 mr-2" />
            Custom Connectors
          </h2>
          <p className="text-slate-600 dark:text-slate-400 mt-1">
            Manage custom data connections and sync schedules
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={fetchConnections}
            className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors flex items-center"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </button>
          <button
            onClick={() => setShowForm(true)}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 transition-colors flex items-center"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Connection
          </button>
        </div>
      </div>

      {/* Access Required Banner */}
      <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Lock className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-amber-900 dark:text-amber-100 mb-1">
              Access Key Required
            </h3>
            <p className="text-sm text-amber-800 dark:text-amber-200">
              Custom connectors require an access key or special permission to use. Please contact support to enable connector access for your account.
            </p>
          </div>
        </div>
      </div>

      {/* Connections List - Always show empty state */}
      <div className="text-center py-12 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg">
        <ShoppingCart className="w-12 h-12 text-slate-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
          No Custom Connections
        </h3>
        <p className="text-slate-600 dark:text-slate-400 mb-4">
          Connect your custom data sources to start syncing data
        </p>
        <button
          onClick={() => setShowForm(true)}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 transition-colors inline-flex items-center"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add First Connection
        </button>
      </div>
    </div>
  );
}

export default MintsoftConnectorsTab;
